import { useCallback, useEffect, useRef, useState } from "react"
import { NeoComponents } from "@forcepoint/platform-utilityui"
import { subMenuItemClick } from "./Sidebar.utils"
import { CollapsedMenuPopupProps, MenuItemData } from "./Sidebar.types"
import { coreStore } from "@forcepoint/platform-coreui"

const getPopupStyles = (position, menuCount) => {
  const pos = { ...position }
  const menuLen = (17.4 + 8 + 8) * menuCount // 17.4 is the height of each menu item, 8 is padding and margin
  const tooltipHeight = menuLen + 32 + 16
  if (tooltipHeight > pos.spaceBelow && pos.spaceAbove >= tooltipHeight) {
    pos.y = pos.y - tooltipHeight + pos.height
  }
  return {
    left: pos.x - 10,
    top: pos.y
  }
}

const CollapsedMenuPopup = ({
  parentApplicationCode,
  hoveredMenu,
  isOpen,
  position,
  menuHeader
}: CollapsedMenuPopupProps) => {
  const popupRef = useRef(null)

  return (
    <div
      style={{
        position: "fixed",
        maxHeight: "400px",
        ...getPopupStyles(position, hoveredMenu?.menus?.length ?? 0)
      }}
      ref={popupRef}
    >
      {isOpen && (
        <div
          className={`collapse-popup-container ${isOpen ? "" : "popup-hidden"}`}
        >
          <div className="popup-title">{menuHeader}</div>
          <div className="popup-submenus-items">
            {hoveredMenu?.menus?.map((menuItem) => (
              <CollapsedMenuPopupItem
                key={menuItem.name}
                hoveredMenu={hoveredMenu}
                parentApplicationCode={parentApplicationCode}
                position={position}
                menuItem={menuItem}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default CollapsedMenuPopup

const CollapsedMenuPopupItem = ({
  menuItem,
  parentApplicationCode,
  hoveredMenu,
  position
}) => {
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false)
  const [currentYCoordinate, setCurrentYCoordinate] = useState(0)

  const getActiveMenuClassName = useCallback(
    (menuItem: MenuItemData, prefixedActiveMenuName: string) => {
      // Early return if no menu item
      if (!menuItem) return ""

      let openedMenuName: string

      // Get active app from store
      const activeApp = coreStore.getState()?.activeApp

      // Determine the correct menu name based on application context
      if (parentApplicationCode === "settings") {
        // For settings menus, check if this menu exists in active app's settings
        const settingsMenu = activeApp?.settings?.find(
          (setting) => setting.name === menuItem.name
        )

        if (!settingsMenu) return ""

        openedMenuName = activeApp?.applicationCode + menuItem.name
      } else {
        // For non-settings menus, use parent application code
        openedMenuName = parentApplicationCode + menuItem.name
      }

      // Check if this menu is the active one
      if (openedMenuName === prefixedActiveMenuName) {
        return "active-popup-menus"
      }

      // Check nested menus recursively
      if (menuItem?.menus?.length > 0) {
        const hasActiveChild = menuItem.menus.some(
          (subMenu) =>
            getActiveMenuClassName(subMenu, prefixedActiveMenuName) !== ""
        )

        if (hasActiveChild) {
          return "active-popup-menus"
        }
      }

      return ""
    },
    [parentApplicationCode]
  )

  const [activeMenuClassName, setActiveMenuClassName] = useState(
    getActiveMenuClassName(
      menuItem,
      coreStore.getState()?.activeApp?.applicationCode +
        coreStore.getState()?.activeMenu?.name
    )
  )

  // Set up a store listener to update active menu class name on relevant store changes
  useEffect(() => {
    const unsubscribe = coreStore.subscribe(() => {
      const activeApp = coreStore.getState()?.activeApp
      const activeMenu = coreStore.getState()?.activeMenu
      const prefixedActiveMenuName =
        activeApp?.applicationCode + activeMenu?.name
      setActiveMenuClassName(
        getActiveMenuClassName(menuItem, prefixedActiveMenuName)
      )
    })
    return unsubscribe
  }, [])
  return (
    <div
      className={`popup-menus ${activeMenuClassName}`}
      onKeyDown={() => {}}
      onClick={(ev) => {
        subMenuItemClick(menuItem, hoveredMenu, setIsSubmenuOpen)
        ev.stopPropagation()
      }}
      onMouseEnter={(ev) => {
        const pos = ev.currentTarget.getBoundingClientRect()
        setCurrentYCoordinate(pos.y)
        if (menuItem.menus?.length > 0) {
          setIsSubmenuOpen(true)
        }
      }}
      onMouseLeave={() => {
        setIsSubmenuOpen(false)
      }}
      key={menuItem.localizationKey || menuItem.name}
    >
      {menuItem.localizationKey || menuItem.name}
      {menuItem.menus && menuItem.menus?.length > 0 && isSubmenuOpen && (
        <div className="collapsed-nested-menu">
          <CollapsedMenuPopup
            menuHeader={menuItem.localizationKey || menuItem.name}
            parentApplicationCode={parentApplicationCode}
            hoveredMenu={menuItem}
            isOpen={isSubmenuOpen}
            position={{
              ...position,
              x: position.x + 190,
              y: currentYCoordinate
            }}
          />
        </div>
      )}
      {menuItem.menus && menuItem.menus?.length > 0 && (
        <NeoComponents.NeoIcon name="nav_caret" size={8} fill="#000" />
      )}
    </div>
  )
}
